import React, { useState, Suspense, useCallback } from 'react'
import { Loader2, Zap, MapPin, User, Settings, LifeBuoy, Plus, UserPlus } from 'lucide-react'
import { useWebSocket } from '../hooks/useWebSocket'
import Sidebar from '../components/Sidebar'
import { UniversalTopBar } from '../components/Topbar'
import NotificationContainer from '../components/ui/NotificationContainer'
import useNotifications from '../hooks/useNotifications'
import VehiclesIcon from '../svg/Vehicles icon.svg'
import OperationsIcon from '../svg/Operations icon.svg'
import SettingIcon from '../svg/Setting icon.svg'
import ProfileIcon from '../svg/Profile icon.svg'
import CommandCenterTrackingSimulator from '../components/commandcenter/CommandCenterTrackingSimulator'
import CommandCenterBookingPage from '../components/commandcenter/CommandCenterBookingPage'

// Lazy load the copied Command Center section pages
const CommandCenterProfile = React.lazy(() => import('../components/commandcenter/CommandCenterProfile'))
const CommandCenterSettings = React.lazy(() => import('../components/commandcenter/CommandCenterSettings'))
const CommandCenterRaiseTicket = React.lazy(() => import('../components/commandcenter/CommandCenterRaiseTicket'))
const CommandCenterOnboardingPage = React.lazy(() => import('../components/commandcenter/CommandCenterOnboardingPage'))

const menuSections = [
  {
    title: 'BOOKING & TRACKING',
    items: [
      { id: 'instant_booking', icon: Zap, label: 'Booking' },
      { id: 'tracking', icon: MapPin, label: 'Trips' },
    ]
  },
  {
    title: 'OPERATIONS',
    items: [
      { id: 'onboarding', icon: UserPlus, label: 'Onboarding' },
    ]
  },
  {
    title: 'SUPPORT & ACCOUNT',
    items: [
      { id: 'raise_ticket', icon: LifeBuoy, label: 'Raise a Ticket' },
      { id: 'settings', icon: Settings, label: 'Settings' },
      { id: 'profile', icon: User, label: 'Profile' },
    ]
  }
]

const menuItems = menuSections.flatMap((sec) => sec.items)

const sectionLoadingLabel = {
  tracking: 'trips',
  instant_booking: 'booking',
  onboarding: 'onboarding',
  raise_ticket: 'support ticket',
  settings: 'settings',
  profile: 'profile',
}

const SectionLoader = ({ section }) => (
  <div className="flex items-center justify-center py-6 sm:py-8">
    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
    <span className="text-gray-600 text-sm sm:text-base">Loading {sectionLoadingLabel[section] || section}...</span>
  </div>
)

const CommandCenterDashboard = ({ dashboardType = 'commandCenter' }) => {
  const [selectedSidebarItem, setSelectedSidebarItem] = useState('instant_booking')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  // Set when the Trips page's Edit button is clicked on a driver-less
  // booking — carries the booking id into the Booking page so it can load
  // and edit that record instead of starting a blank one.
  const [editBookingId, setEditBookingId] = useState(null)
  // Set alongside the switch to the Trips tab whenever it's reached as the
  // result of a Booking-page action completing (Save Later, a driver
  // accepting, Add & Assign, etc.) — tells the Trips page which pipeline tab
  // that action's booking actually landed in, instead of always opening on
  // Pending regardless of what just happened. Reset to null once consumed so
  // a later plain sidebar click into Trips still gets the normal default.
  const [tripsInitialStage, setTripsInitialStage] = useState(null)

  const { notification, showSuccess, showError, showWarning } = useNotifications()
  // Establishes the Socket.IO connection for this session (same pattern as
  // HospitalDashboard.jsx/FleetDashboard.jsx) — the Trips page's live map
  // needs this connected before it can join a booking room and receive
  // driver_location_updated events.
  useWebSocket()

  const handleSidebarItemChange = useCallback((itemId) => {
    setSelectedSidebarItem(itemId)
    setIsMobileMenuOpen(false)
    // Sidebar navigation is an explicit "go to this section" choice, not a
    // continuation of a Trips-row Edit click — always start the Booking
    // page fresh from here, never silently resume a stale edit.
    setEditBookingId(null)
    // Same reasoning — a plain sidebar click into Trips should open on the
    // normal default tab, not silently reuse whatever stage a previous
    // Booking-page action last requested.
    setTripsInitialStage(null)

    const sectionNames = {
      tracking: 'Trips',
      instant_booking: 'Booking',
      onboarding: 'Onboarding',
      raise_ticket: 'Raise a Ticket',
      settings: 'Settings',
      profile: 'Profile',
    }
    showSuccess('Navigation', `Switched to ${sectionNames[itemId] || itemId} section`)
  }, [showSuccess])

  const handleBookingButtonClick = useCallback(() => {
    setEditBookingId(null)
    setTripsInitialStage(null)
    setIsMobileMenuOpen(false)
    if (selectedSidebarItem !== 'instant_booking') {
      setSelectedSidebarItem('instant_booking')
      showSuccess('Navigation', 'Switched to Booking')
    } else if (editBookingId) {
      showSuccess('Booking', 'Started new booking')
    }
  }, [selectedSidebarItem, editBookingId, showSuccess])

  const handleOnboardingAction = useCallback((message, title) => {
    showSuccess(title || 'Success', message)
  }, [showSuccess])

  const handleOnboardingError = useCallback((message, title) => {
    showError(title || 'Error', message)
  }, [showError])

  const isBookingActive = selectedSidebarItem === 'instant_booking' && !editBookingId

  const bookingTopBarButton = (
    <button
      type="button"
      id="topbar-booking-btn"
      onClick={handleBookingButtonClick}
      className={`inline-flex items-center gap-1.5 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer select-none active:scale-[0.98] whitespace-nowrap shadow-xs hover:shadow text-white ${
        isBookingActive
          ? 'bg-blue-700 ring-2 ring-blue-400/40'
          : 'bg-blue-600 hover:bg-blue-700'
      }`}
      title={editBookingId ? 'Start New Fresh Booking' : 'Navigate to Booking'}
      aria-label="Booking"
    >
      <Plus className="w-4 h-4 stroke-[2.5]" />
      <span>{editBookingId ? 'New Booking' : 'Booking'}</span>
    </button>
  )

  const renderContent = () => {
    switch (selectedSidebarItem) {
      case 'instant_booking':
        return (
          <CommandCenterBookingPage
            editBookingId={editBookingId}
            onAction={(message, title) => showSuccess(title || 'Success', message)}
            onError={(message, title) => showError(title || 'Error', message)}
            onNavigateToTrips={(stage) => {
              setEditBookingId(null)
              setTripsInitialStage(stage || null)
              setSelectedSidebarItem('tracking')
            }}
          />
        )

      case 'tracking':
        return (
          <CommandCenterTrackingSimulator
            initialStage={tripsInitialStage}
            onCreateBooking={(bookingId) => {
              setEditBookingId(bookingId || null)
              setSelectedSidebarItem('instant_booking')
              showSuccess('Navigation', bookingId ? 'Editing booking' : 'Switched to Booking')
            }}
            onAction={(message, title) => showSuccess(title || 'Success', message)}
            onError={(message, title) => showError(title || 'Error', message)}
            onWarning={(message, title) => showWarning(title || 'Scheduled Ride Alert', message)}
          />
        )

      case 'onboarding':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={<SectionLoader section="onboarding" />}>
              <CommandCenterOnboardingPage
                onAction={handleOnboardingAction}
                onError={handleOnboardingError}
              />
            </Suspense>
          </div>
        )

      case 'raise_ticket':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={<SectionLoader section="raise_ticket" />}>
              <CommandCenterRaiseTicket
                onSubmit={(ticket) => showSuccess('Support', `Ticket #${String(ticket.ticket_number || ticket.id).replace(/^TKT-/, '')} submitted successfully`)}
                onAction={(message, title) => showSuccess(title || 'Success', message)}
              />
            </Suspense>
          </div>
        )

      case 'profile':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={<SectionLoader section="profile" />}>
              {/* Wallet/Feedback/Terms/About/Notifications have no real screen
                  behind them yet — no-ops rather than a fake "success" toast,
                  matching onPasswordChangeClick (the real password flow is
                  handled inside CommandCenterProfile itself). */}
              <CommandCenterProfile
                onWalletClick={() => {}}
                onFeedbackClick={() => {}}
                onPasswordChangeClick={() => {}}
                onTermsClick={() => {}}
                onAboutClick={() => {}}
                onNotificationClick={() => {}}
              />
            </Suspense>
          </div>
        )

      case 'settings':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={<SectionLoader section="settings" />}>
              <CommandCenterSettings />
            </Suspense>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex h-screen" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
    }}>
      <Sidebar
        sections={menuSections}
        menuItems={menuItems}
        selectedItem={selectedSidebarItem}
        onItemClick={handleSidebarItemChange}
        defaultSelectedItem="instant_booking"
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={setIsMobileMenuOpen}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-2 md:px-3 lg:px-3 relative z-30">
          <UniversalTopBar
            title="Command Center Dashboard"
            subtitle="Trips, booking, support, and account management."
            userRole="commandCenter"
            onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            isMobileMenuOpen={isMobileMenuOpen}
            actionButton={bookingTopBarButton}
            hideUserDropdown={true}
          />
        </div>

        <main className="flex-1 overflow-y-auto px-2 md:px-3 lg:px-3 pt-2.5 pb-4">
          {renderContent()}
        </main>
      </div>

      <NotificationContainer notification={notification} />
    </div>
  )
}

export default CommandCenterDashboard